import type { RootState } from 'RootTypes';
import { createSelector } from 'reselect';
import { isSeasonOnOrAfter } from '@common/season';
import { isDefined } from '@util/types';
import * as TeamMember from '../teammember';
import { emptyTeamStats } from './reducer';

import type {
    TeamID,
    TeamListGroupedBySeason,
    TeamOrOrgListGroupedBySeason,
    ListTeam,
    SeasonAndYear,
    OrgLandingPageTeam,
    Sport,
    PublicTeamID,
} from './types';
import { NAMESPACE, isListTeam, sportIsValid, isAdminOfListItem } from './types';

export const getTeam = (teamID: TeamID | PublicTeamID) => (state: RootState) => {
    return state[NAMESPACE].list.find((team) => team.id === teamID || team.publicID === teamID);
};

export const getMeTeamsAndOrgs = (state: RootState): TeamOrOrgListGroupedBySeason[] | undefined =>
    state[NAMESPACE].meTeamsAndOrgs;

const getMeTeams = createSelector(getMeTeamsAndOrgs, (teamsAndOrgs): TeamListGroupedBySeason[] =>
    (teamsAndOrgs ?? [])
        .flatMap((seasonTeam) =>
            Object.keys(seasonTeam).map((season) => {
                const applicableTeams = seasonTeam[season].filter(isListTeam);
                return applicableTeams.length > 0 ? { [season]: applicableTeams } : undefined;
            }),
        )
        .filter(isDefined),
);

export const getMeTeamsList = createSelector(getMeTeams, (meTeams) =>
    meTeams.flatMap((season) => Object.values(season)).flat(),
);

export const getMeTeamsWithValidSportsByTeamGroup = (teamGroup: TeamMember.TeamGroup) =>
    createSelector(getMeTeams, (meTeams) =>
        meTeams
            .flatMap((seasonTeam) =>
                Object.keys(seasonTeam).map((season) => {
                    const applicableTeams = seasonTeam[season].filter(
                        (team) => team.teamGroups.includes(teamGroup) && sportIsValid(team.sport),
                    );
                    return applicableTeams.length > 0 ? { [season]: applicableTeams } : undefined;
                }),
            )
            .filter(isDefined),
    );

export const getSeasonStats = (state: RootState) => state[NAMESPACE].seasonStats;
export const getGameStatsForTeam = (teamID: TeamID) => (state: RootState) =>
    state[NAMESPACE].gameStats[teamID] || emptyTeamStats;
export const getPlayerStats = (state: RootState) => state[NAMESPACE].playerStats;
export const getGameStats = (teamID: TeamID, opponentTeamID: TeamID) => (state: RootState) => {
    const teamStats = getGameStatsForTeam(teamID)(state);
    const opponentTeamStats = getGameStatsForTeam(opponentTeamID)(state);
    return { teamStats, opponentTeamStats };
};

export const getMembers = (teamID: TeamID, group: TeamMember.TeamGroup) => (state: RootState) => {
    return state[TeamMember.NAMESPACE].teamMemberUsers.filter((teamMember) => {
        const teamMatches = teamMember.teamID == teamID;
        const groupMatches = teamMember.teamGroups.includes(group);
        return teamMatches && groupMatches;
    });
};

export const getSelectedTeamID = (state: RootState) => state[NAMESPACE].selectedTeam.id;

export const getSelectedTeam = (state: RootState) => state[NAMESPACE].selectedTeam;

export const getActiveOpponents = (state: RootState) =>
    state[NAMESPACE].opponents?.filter((opponent) => !opponent.isHidden);
export const getSelectedOpponent = (state: RootState) => state[NAMESPACE].selectedOpponent;

export const getOrgLandingPageTeamList = (
    oldestSeason: SeasonAndYear,
    sport: Sport | undefined,
    activeTeamIDs: TeamID[],
) =>
    createSelector(getMeTeams, (meTeams): TeamListGroupedBySeason<OrgLandingPageTeam>[] => {
        const isCorrectSport = (lt: ListTeam) => lt.sport === sport;
        const isNotTooOld = (lt: ListTeam) =>
            isSeasonOnOrAfter({ season: lt.seasonName, year: lt.seasonYear }, oldestSeason);

        return meTeams
            .flatMap((seasonTeam) =>
                Object.keys(seasonTeam).map((season) => {
                    const applicableTeams = seasonTeam[season]
                        .filter((team) => isAdminOfListItem(team) && isCorrectSport(team) && isNotTooOld(team))
                        .map(
                            (t): OrgLandingPageTeam => ({
                                ...t,
                                hasJoinedOrg: activeTeamIDs.find((id) => id === t.id) !== undefined,
                            }),
                        );
                    return applicableTeams.length > 0 ? { [season]: applicableTeams } : undefined;
                }),
            )
            .filter(isDefined);
    });
